/**
 * Canonical status/tag colour mapping — ONE state = ONE colour everywhere in
 * the admin app (see CLAUDE.md "UI conventions"). Extend the map here; do
 * not add per-component severity switches.
 */
export type TagSeverity = 'success' | 'info' | 'warning' | 'danger';

const STATUS_SEVERITY: Record<string, TagSeverity> = {
  // healthy / terminal-good
  active: 'success',
  paid: 'success',
  resolved: 'success',
  completed: 'success',
  rewarded: 'success',
  won_back: 'success',
  low: 'success',
  processed: 'success',
  succeeded: 'success',
  // in-flight / neutral
  in_progress: 'info',
  promise_active: 'info',
  assigned: 'info',
  redeemed: 'info',
  processing: 'info',
  medium: 'info',
  auto_closed: 'info',
  scheduled: 'info',
  submitted: 'info',
  refunded: 'info',
  // needs attention
  open: 'warning',
  pending: 'warning',
  unpaid: 'warning',
  overdue: 'warning',
  past_overdue: 'warning',
  high: 'warning',
  on_hold: 'warning',
  requested: 'warning',
  new: 'warning',
  on_break: 'warning',
  requires_action: 'warning',
  partially_refunded: 'warning',
  // bad / terminal-bad
  critical: 'danger',
  urgent: 'danger',
  suspended_payment: 'danger',
  suspended: 'danger',
  failed: 'danger',
  cancelled: 'danger',
  churned: 'danger',
  unsuccessful: 'danger',
  broken: 'danger',
  inactive: 'danger',
  expired: 'danger',
  error: 'danger',
  offline: 'danger',
  disputed: 'danger'
};

export function statusSeverity(
  status: string | null | undefined,
  fallback: TagSeverity = 'info'
): TagSeverity {
  if (!status) return fallback;
  return STATUS_SEVERITY[String(status).toLowerCase()] ?? fallback;
}

/**
 * Communication log delivery statuses. Kept out of STATUS_SEVERITY because
 * "sent"/"queued" mean different things elsewhere (e.g. project stock/PO
 * "sent") and adding them there would recolour those tags app-wide.
 */
const COMMUNICATION_STATUS_SEVERITY: Record<string, TagSeverity> = {
  sent: 'success',
  queued: 'warning',
  // Deliberately not dispatched (non-production / kill switch) - logged only.
  blocked: 'warning',
  failed: 'danger'
};

export function communicationStatusSeverity(status: string | null | undefined): TagSeverity {
  if (!status) return 'info';
  return COMMUNICATION_STATUS_SEVERITY[String(status).toLowerCase()] ?? 'info';
}

/**
 * TOC "type" and "sequence" are categorical attributes, not health states —
 * they get their own fixed colours (never derived from STATUS_SEVERITY) so
 * they never visually collide with the semantic status pill sitting next to
 * them. Deliberately outside the green/blue/orange/red severity palette.
 *
 * toc_type gets one colour PER TYPE (activation/regrade/cancellation/
 * relocation/me_toc are different jobs and must be visually distinguishable
 * from each other, not just from toc_status).
 */
const TOC_TYPE_COLOURS: Record<string, string> = {
  activation: '#4F46E5', // indigo
  regrade: '#0D9488', // teal
  cancellation: '#BE123C', // rose
  relocation: '#7E22CE', // purple
  me_toc: '#334155' // slate
};

const TOC_TYPE_DEFAULT_COLOUR = '#4F46E5';

export function tocTypeTagStyle(tocType: string | null | undefined): Record<string, string> {
  const colour = (tocType && TOC_TYPE_COLOURS[String(tocType).toLowerCase()]) || TOC_TYPE_DEFAULT_COLOUR;
  return { 'background-color': colour, color: '#ffffff' };
}

export const TOC_SEQUENCE_TAG_STYLE: Record<string, string> = {
  'background-color': '#64748B',
  color: '#ffffff'
};
